import React from 'react';
import { Section, ThreeUp, MultiView, AgentsDiagram, LeadCRM, CollabPreview, Integrations, Pricing } from './components/Sections.jsx';
import { DitherCanvas, DitherFields } from './components/Dither.jsx';
import { AIBuilder } from './components/AIBuilder.jsx';
import { StartFreeSplit } from './components/StartFreeSplit.jsx';

const URLS = (typeof window !== 'undefined' && window.JINBAN_URLS) || {};
const ASSETS = URLS.assets || '/static/landing/assets';

function HeroDither() {
  return <DitherCanvas field={DitherFields.horizon} pixelSize={3} fps={14} fg="#0A0A0A" />;
}

export default function App() {
  return (
    <>
      <nav className="nav">
        <div className="nav__inner">
          <a href={URLS.home || '/'} className="nav__logo">
            <img src={`${ASSETS}/logo-bw.png`} alt="" />
            <span>jinban.ai</span>
          </a>
          <div className="nav__links">
            <a href="#kanban">Boards</a>
            <a href="#agents">Agents</a>
            <a href="#crm">Lead CRM</a>
            <a href="#sovereignty">EU sovereignty</a>
            <a href="#pricing">Pricing</a>
            <a href={URLS.docs || '#docs'}>Docs</a>
          </div>
          <div className="nav__cta">
            <a href={URLS.signin || '#signin'}>Sign in</a>
            <a className="btn btn--gold" href={URLS.startFree || '#start'}>Start free</a>
          </div>
        </div>
      </nav>

      <section className="hero" data-screen-label="Hero">
        <div className="hero__dither" style={{ opacity: 0.12 }}>
          <HeroDither />
        </div>
        <div className="container hero__inner">
          <div className="hero__eyebrow">
            <span className="dot" />
            <span>EU sovereign workspace · GDPR · self-hostable</span>
          </div>
          <h1>One board for the <em>pipeline</em>, the sprint, and the conversation.</h1>
          <p className="hero__sub">
            Built-in CRM with AI lead enrichment, trigger-based automation, and voice huddles — all on the same board. Hosted in the EU, on infrastructure you can audit.
          </p>
          <div className="hero__ctas">
            <StartFreeSplit />
            <a className="btn btn--ghost" href="#kanban">See a live board</a>
            <span className="meta">no credit card · 60-second setup</span>
          </div>

          <AIBuilder />
        </div>
      </section>

      <div className="logo-strip">
        <div className="logo-strip__inner">
          <span className="logo-strip__label">Trusted by EU teams</span>
          <div className="logo-strip__logos">
            <span>HANSEATIC</span>
            <span>Lyra AB</span>
            <span>Zentrum</span>
            <span>Northwind</span>
            <span>Stockholms Bryggeri</span>
            <span>Acme GmbH</span>
          </div>
        </div>
      </div>

      <Section
        id="why"
        label="Four tools, one board"
        title={<>Stop paying for <em>four</em> tools that should be one.</>}
        lede="Most teams glue a kanban to a CRM to a chat tool to a Zapier-style automator. We don't. Pipelines, projects, conversations, and AI automation live on the same surface."
      >
        <ThreeUp />
      </Section>

      <Section
        id="kanban"
        label="One board, four ways"
        title={<>Columns. Swimlanes. Sprint. Table. <em>No data duplicated.</em></>}
        lede="The same cards, the same source of truth — rendered for the way each person works. Switch views without leaving the board."
      >
        <MultiView />
      </Section>

      <Section
        id="agents"
        label="AI agents · sandboxed"
        title={<>Trigger automation that <em>actually</em> moves work.</>}
        lede="Build agentic flows visually — no Zapier, no glue code. Trigger from a column, a chat command, a schedule, or an inbound event. Every step runs in a Kubernetes sandbox with gVisor — your data never leaves your tenant."
        dark
      >
        <AgentsDiagram />
      </Section>

      <Section
        id="crm"
        label="Built-in lead CRM"
        title={<>The CRM, with <em>AI agents</em> baked in.</>}
        lede="A leads board lives next to your task boards. Same drag-and-drop, same chat, same agents — plus AI enrichment, ICP scoring, and outreach drafting on every lead. No more stitching together a CRM, an enrichment tool, and a sequencer."
      >
        <LeadCRM />
      </Section>

      <Section
        id="collab"
        label="Real-time collab"
        title={<>Chat, voice, and AI in <em>the same place</em> as the work.</>}
        lede="Start a voice huddle from a card. Threads attach to tasks. The AI listens and offers next steps — you approve. Slack-grade collab without leaving the board."
      >
        <CollabPreview />
      </Section>

      <section className="section section--dark" id="sovereignty" data-screen-label="EU Sovereignty">
        <div className="container">
          <div className="section__label">EU sovereign · digital independence</div>
          <h2 className="section__title">Built where your <em>data</em> lives.</h2>
          <p className="section__lede">
            Hosted in Frankfurt and Paris on EU-owned infrastructure. No US Cloud Act exposure.
            Bring-your-own LLM (Mistral, Aleph Alpha, your own). Single-tenant deployments for regulated industries.
          </p>

          <div className="sovereignty">
            <div className="sov-map">
              <DitherCanvas field={DitherFields.plasma} pixelSize={3} fps={10} fg="#FFE44D" bg="#000" />
              <div className="sov-points">
                <div className="sov-point" style={{ top: '38%', left: '52%' }}>
                  <div className="sov-point__dot" />
                  <span>FRA</span>
                </div>
                <div className="sov-point" style={{ top: '46%', left: '46%' }}>
                  <div className="sov-point__dot" />
                  <span>PAR</span>
                </div>
                <div className="sov-point" style={{ top: '32%', left: '50%' }}>
                  <div className="sov-point__dot" />
                  <span>AMS</span>
                </div>
                <div className="sov-point" style={{ top: '50%', left: '60%' }}>
                  <div className="sov-point__dot" />
                  <span>VIE</span>
                </div>
              </div>
            </div>

            <div className="sov-list">
              <div className="sov-list__item">
                <div className="sov-list__item-num">01 / DATA</div>
                <div>
                  <h4>EU-resident, full stop.</h4>
                  <p>Every byte — primary, replicas, backups, logs — stays inside the EU. CloudNativePG on Kubernetes, not a managed US service in disguise.</p>
                </div>
              </div>
              <div className="sov-list__item">
                <div className="sov-list__item-num">02 / MODELS</div>
                <div>
                  <h4>Bring-your-own LLM.</h4>
                  <p>Default to Mistral or Aleph Alpha. Connect a self-hosted endpoint. We never train on your prompts.</p>
                </div>
              </div>
              <div className="sov-list__item">
                <div className="sov-list__item-num">03 / OPS</div>
                <div>
                  <h4>Auditable from the metal up.</h4>
                  <p>ArgoCD GitOps, signed container images, every change in version control. SOC 2-style controls without the US-incumbent overhead.</p>
                </div>
              </div>
              <div className="sov-list__item">
                <div className="sov-list__item-num">04 / EXIT</div>
                <div>
                  <h4>No lock-in. Take your data.</h4>
                  <p>Full REST API, scheduled exports, Postgres dumps on request. Open data formats. Helm charts available for self-hosting.</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <Section
        id="integrations"
        label="Integrations"
        title={<>Native, signed, and <em>two-way</em>.</>}
        lede="Real webhooks with signature validation. Bidirectional syncs. Encrypted credentials at the board level. No middleware."
      >
        <Integrations />
      </Section>

      <Section
        id="security"
        label="Security & scale"
        title="Quiet trust signals, for the buyer in the room."
        dense
      >
        <div className="sec-grid">
          {[
            ['Antivirus', 'Every uploaded file scanned before it touches a board.'],
            ['Encrypted secrets', 'Fernet-encrypted at board and org scope, runtime substitution.'],
            ['gVisor sandbox', 'Agent runs isolated per-job with network allowlist.'],
            ['RBAC', 'Admin / write / read board roles. Row-level filtering on hidden columns.'],
            ['SSO + SCIM', 'Available on Sovereign tier. SAML, OIDC, SCIM 2.0.'],
            ['Audit log', 'Every state change versioned. Exportable as JSON.'],
            ['Signed webhooks', 'HMAC payloads, replay protection, configurable retries.'],
            ['Rate limiting', 'Per-org, per-token. DRF throttling. Burstable quotas.'],
          ].map(([title, sub]) => (
            <div key={title} className="sec-item">
              <div className="sec-item__title">{title}</div>
              <div className="sec-item__sub">{sub}</div>
            </div>
          ))}
        </div>
      </Section>

      <Section
        id="pricing"
        label="Pricing"
        title={<>Simple. Per-seat. <em>EU-priced.</em></>}
        lede="Free for one. Flat for teams. Talk to us if you need single-tenant or self-hosting."
      >
        <Pricing ctaUrl={URLS.startFree} />
      </Section>

      <section className="final-cta" data-screen-label="Final CTA">
        <div className="final-cta__dither">
          <DitherCanvas field={DitherFields.diagonal} pixelSize={3} fps={12} fg="#FFE44D" />
        </div>
        <div className="container final-cta__inner">
          <h2>Run your <em>pipeline</em> and your <em>sprint</em> on the same board.</h2>
          <p>Start free in 60 seconds. No credit card. Hosted in the EU, on infrastructure you can audit.</p>
          <form
            className="final-cta__form"
            method="post"
            action={URLS.magicLink || '/auth/magic-link/'}
          >
            {URLS.csrfToken && (
              <input type="hidden" name="csrfmiddlewaretoken" value={URLS.csrfToken} />
            )}
            <input type="email" name="email" placeholder="you@company.eu" required />
            <button type="submit">Start free →</button>
          </form>
        </div>
      </section>

      <footer className="footer">
        <div className="container">
          <div className="footer__grid">
            <div className="footer__brand">
              <img src={`${ASSETS}/logo-bw.png`} alt="jinban.ai" />
              <p>An EU-sovereign virtual workspace bringing humans and AI agents together on one board.</p>
              <p style={{ fontFamily: 'var(--font-mono)', fontSize: '12px' }}>
                Made in the EU · ▮▮▮▮▮▮ on track
              </p>
            </div>
            <div className="footer__col">
              <h5>Product</h5>
              <ul>
                <li><a href="#kanban">Boards</a></li>
                <li><a href="#agents">Agents</a></li>
                <li><a href="#crm">Lead CRM</a></li>
                <li><a href="#collab">Voice huddles</a></li>
                <li><a href="#integrations">Integrations</a></li>
              </ul>
            </div>
            <div className="footer__col">
              <h5>Company</h5>
              <ul>
                <li><a href="#why">About</a></li>
                <li><a href="#sovereignty">Manifesto</a></li>
                <li><a href="#why">Customers</a></li>
                <li><a href="mailto:hello@jinban.ai">Careers</a></li>
              </ul>
            </div>
            <div className="footer__col">
              <h5>Resources</h5>
              <ul>
                <li><a href={URLS.docs || '#'}>Docs</a></li>
                <li><a href={URLS.docs || '#'}>REST API</a></li>
                <li><a href="/healthz/">Status</a></li>
                <li><a href="#why">Changelog</a></li>
              </ul>
            </div>
            <div className="footer__col">
              <h5>Legal</h5>
              <ul>
                <li><a href="#sovereignty">Privacy</a></li>
                <li><a href="#sovereignty">Terms</a></li>
                <li><a href="#sovereignty">DPA</a></li>
                <li><a href="#security">Security</a></li>
              </ul>
            </div>
          </div>
          <div className="footer__bottom">
            <span>© 2026 jinban.ai · Frankfurt, Paris, Amsterdam</span>
            <span>v0.9.4 · all systems operational</span>
          </div>
        </div>
      </footer>
    </>
  );
}
